import React, { useState, forwardRef, useMemo } from 'react';
import { TbEye, TbEyeOff } from 'react-icons/tb';
import InputMask from 'react-input-mask';
import {
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
  Icon,
  Text,
  VStack,
  Spinner,
} from '@chakra-ui/react';

interface SimpleInputProps extends InputProps {
  label?: string;
  error?: string;
  isLoading?: boolean;
  labelColor?: string;
}

const SimpleInput = forwardRef<HTMLInputElement | null, SimpleInputProps>(
  (props, ref) => {
    const {
      variant,
      type,
      isDisabled,
      label,
      error,
      labelColor,
      isLoading,
      id,
    } = props;
    const [showPassword, setShowPassword] = useState<boolean>(false);

    const getInputType = () => {
      if (type === 'password' && !showPassword) {
        return 'password';
      }

      if (type === 'time') {
        return 'time';
      }

      return 'text';
    };

    const getIconColor = () => {
      if (variant === 'dark') {
        if (isDisabled) return 'gray.500';

        return 'gray.300';
      } else {
        if (isDisabled) return 'gray.100';

        return 'gray.400';
      }
    };

    const mask = useMemo(() => {
      switch (type) {
        case 'cnpj':
          return '99.999.999/9999-99';
        case 'cpf':
          return '999.999.999-99';
        case 'year':
          return '9999';
        case 'phone':
          return '(99) 9 9999-9999';
        case 'date':
          return '99/99/9999';
        case 'distance':
          return '999.999.999';
        case 'plate':
          return 'aaa9*999';
        default:
          return undefined;
      }
    }, [type]);

    return (
      <VStack {...props} spacing="4px" w="100%">
        {label && (
          <Text
            fontSize="sm"
            fontWeight={400}
            color={labelColor || 'gray.300'}
            alignSelf="flex-start"
          >
            {label}
          </Text>
        )}
        <InputGroup variant={variant}>
          {mask ? (
            <Input
              mask={mask}
              maskChar={null}
              as={InputMask}
              alwaysShowMask={false}
              ref={ref}
              {...props}
              mb={0}
              w="100%"
              type={getInputType()}
              data-cy={id}
            />
          ) : (
            <Input
              data-cy={id}
              ref={ref}
              {...props}
              mb={0}
              w="100%"
              type={getInputType()}
            />
          )}
          {isLoading ? (
            <InputRightElement
              mr="18px"
              onClick={() => setShowPassword(showPassword => !showPassword)}
            >
              <Spinner size="sm" color={labelColor || 'gray.300'} />
            </InputRightElement>
          ) : type === 'password' ? (
            <InputRightElement
              mr="18px"
              onClick={() => setShowPassword(showPassword => !showPassword)}
            >
              <Icon
                as={showPassword ? TbEyeOff : TbEye}
                h={6}
                w={6}
                color={getIconColor()}
                alignSelf="center"
              />
            </InputRightElement>
          ) : (
            <></>
          )}
        </InputGroup>
        {error && (
          <Text
            alignSelf="start"
            mt="-18px"
            pl="8px"
            color="negative"
            fontSize="sm"
          >
            {error}
          </Text>
        )}
      </VStack>
    );
  },
);

SimpleInput.displayName = 'Input';

export default SimpleInput;
